import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { howItWorks } from "../../mock/workflows";
import { useFetch } from "../../lib/api";
import { SectionLabel } from "../shared";

export function WatchSection() {
  return (
    <section className="bg-white border-y border-[#E7E0D2]">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-20">
        <SectionLabel color="#E8590C">Learn faster</SectionLabel>
        <h2 className="font-display font-bold text-[34px] lg:text-[46px] leading-[1.08] tracking-tight max-w-3xl">
          Watch the <span className="text-[#E8590C]">60-second</span> and the <span className="text-[#E5484D]">45-minute</span> version.
        </h2>
        <p className="mt-4 text-[16px] text-[#1A1914]/60 max-w-2xl">Short on time? Pick the format that fits your day. Free to watch live; recordings included with Pro.</p>
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          <Link to="/pulse" className="rp-card border border-[#E8590C]/25 bg-[#E8590C]/[0.04] rounded-2xl p-7 group">
            <span className="font-mono-label text-[10px] px-2.5 py-1 rounded-full border border-[#E8590C]/30 text-[#C2410C]">US Risk Desk · monthly · 45 min</span>
            <h3 className="mt-4 font-display font-semibold text-[24px]">Live monthly webinars</h3>
            <p className="mt-2 text-[14px] leading-relaxed text-[#1A1914]/60">A CRO, former regulator or GRC consultant joins every month. Get the .ics invite, attend live, ask questions. Recordings for Pro members.</p>
            <p className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#C2410C] group-hover:gap-2.5 transition-all">See upcoming sessions <ArrowRight size={14} /></p>
          </Link>
          <Link to="/pulse" className="rp-card border border-[#E5484D]/25 bg-[#E5484D]/[0.04] rounded-2xl p-7 group">
            <span className="font-mono-label text-[10px] px-2.5 py-1 rounded-full border border-[#E5484D]/30 text-[#DC2626]">RiskPedia Shorts · 60 seconds</span>
            <h3 className="mt-4 font-display font-semibold text-[24px]">SEC, CISA, FinCEN — in a minute</h3>
            <p className="mt-2 text-[14px] leading-relaxed text-[#1A1914]/60">Bite-sized explainers on every US compliance obligation. Watch on the train. Five active series. New Shorts weekly.</p>
            <p className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#DC2626] group-hover:gap-2.5 transition-all">Browse the library <ArrowRight size={14} /></p>
          </Link>
        </div>
      </div>
    </section>
  );
}

export function GlossaryPreview() {
  const [q, setQ] = useState("");
  const { data: glossaryTerms } = useFetch("/glossary");
  const shown = (glossaryTerms || []).filter((t) => t.term.toLowerCase().includes(q.toLowerCase())).slice(0, 9);
  return (
    <section className="max-w-[1320px] mx-auto px-5 lg:px-8 py-20">
      <SectionLabel>Live · updated from trending searches</SectionLabel>
      <h2 className="font-display font-bold text-[34px] lg:text-[46px] tracking-tight">The Risk <span className="text-[#3F4CE0]">Glossary</span></h2>
      <p className="mt-4 text-[16px] text-[#1A1914]/60 max-w-2xl">200+ AI-curated definitions across ERM, cyber, operational, financial, ESG, government and emerging risk. Search a term to see a preview.</p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative w-full sm:w-96">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1A1914]/35" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 212+ terms…" data-testid="glossary-preview-search"
            className="w-full pl-10 pr-4 py-3 text-[14px] bg-white border border-[#E7E0D2] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3F4CE0]/40 focus:border-[#3F4CE0]" />
        </div>
        <Link to="/glossary" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#3F4CE0] hover:underline">Browse all terms <ArrowRight size={14} /></Link>
      </div>
      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="glossary-preview-grid">
        {shown.map((t) => (
          <Link key={t.slug} to="/glossary" state={{ open: t.slug }} className="rp-card bg-white border border-[#E7E0D2] rounded-xl p-5 group">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display font-semibold text-[17px] leading-snug group-hover:text-[#3F4CE0] transition-colors">{t.term}</h3>
              <span className="shrink-0 font-mono-label text-[9.5px] px-2 py-0.5 rounded-full bg-[#F3EEE4] text-[#1A1914]/55 border border-[#E7E0D2]">{t.category}</span>
            </div>
            <p className="mt-2.5 text-[13px] leading-relaxed text-[#1A1914]/60">{t.short}</p>
            <p className="mt-3 text-[12.5px] font-semibold text-[#3F4CE0] inline-flex items-center gap-1 group-hover:gap-2 transition-all">Detail <ArrowRight size={12} /></p>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function HowItWorksSection() {
  return (
    <section className="bg-[#F3EEE4] border-y border-[#E7E0D2]">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-20">
        <SectionLabel color="#12B76A">How it works</SectionLabel>
        <h2 className="font-display font-bold text-[34px] lg:text-[44px] leading-[1.1] tracking-tight max-w-3xl">
          A five-question quiz, an AI shortlist, then a sit-down with <span className="text-[#12B76A]">the advisor.</span>
        </h2>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {howItWorks.map((s) => (
            <div key={s.n} className="rp-card bg-white border border-[#E7E0D2] rounded-2xl p-6">
              <p className="font-display font-bold text-[30px] text-[#3F4CE0]/25">{s.n}</p>
              <h3 className="mt-3 font-display font-semibold text-[19px]">{s.title}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[#1A1914]/60">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section className="max-w-[1320px] mx-auto px-5 lg:px-8 py-24 text-left">
      <div className="max-w-3xl">
        <SectionLabel>Built for risk teams</SectionLabel>
        <h2 className="font-display font-bold text-[36px] lg:text-[52px] leading-[1.05] tracking-tight">Find your framework. <span className="text-[#E8590C]">Today.</span></h2>
        <p className="mt-4 text-[16px] text-[#1A1914]/60">Sign up with email or Google. No credit card. Personalised match scores, side-by-side comparison, saved assessments, and Sam — the built-in AI advisor.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/signup" data-testid="final-cta-signup" className="inline-flex items-center gap-2 px-7 py-4 text-[15px] font-semibold text-white bg-[#3F4CE0] hover:bg-[#3341C6] rounded-xl rp-btn shadow-md">Get started <ArrowRight size={16} /></Link>
          <Link to="/login" className="inline-flex items-center px-7 py-4 text-[15px] font-semibold text-[#1A1914] bg-white border border-[#E7E0D2] hover:border-[#1A1914]/40 rounded-xl rp-btn">Log in</Link>
        </div>
      </div>
    </section>
  );
}
